import {type GraphNode, type ParsedRuleGraph, normalizeText, Relationship} from "@packages/commons";
import { filter, find, pull } from "lodash";

const log = (...messages: any[]) => {
  if (process.env.NODE_ENV === "test") {
    return;
  }
  console.warn(...messages);
};

const collectDocIds = (docs: any, seen = new WeakSet()) => {
  const result: string[] = [];
  // return undefined if we get undefined
  if (!docs) return docs;
  for (const doc of Object.values(docs) as any) {
    if (!doc || typeof doc !== "object") continue;
    if (seen.has(doc)) continue;
    seen.add(doc);

    if (doc.reference) result.push(doc.reference);
    else result.push(...collectDocIds(doc, seen));
  }
  return result;
};

const pruneNode = (rule_graph: ParsedRuleGraph, data: GraphNode) => {
  const parents = rule_graph.predecessors(data.id);
  if (parents) {
    for (const p of parents) {
      const p_data = rule_graph.node(p);
      //@ts-ignore
      rule_graph.removeEdge(p.id, data.id);
      p_data.conditions = undefined;
      p_data.rows = undefined;
      p_data.value = undefined;
      p_data.otherwise = undefined;
      rule_graph.setNode(p, p_data);
    }
  }

  rule_graph.removeNode(data.id);
  log("cleanGraph", `Stripping old node '${data.description}'`, { node: data }, undefined, {
    showInTests: false,
  });
};

export const cleanGraph = (
  rule_graph: ParsedRuleGraph,
  sub_graph: ParsedRuleGraph,
  documentId: string,
  documents: any,
  relationships: Relationship[]
): GraphNode[] => {
  const removed: GraphNode[] = [];
  const used_nodes = ((sub_graph.graph() as any)?.usedAttributes || []) as string[];

  const allDocIds = collectDocIds(documents);

  const allNodes = rule_graph.nodes();

  for (const n of allNodes) {
    const data = rule_graph.node(n);
    // we can remove nodes along the way so may not exist anymore
    if (!data) continue;

    if (!data.entity) {
      data.entity = "global";
    }
    // @ts-ignore
    if (data._xgen_id) data._xgen_id = undefined;
    if (data.entity === "global") data.hidden = false;
    if (data.entity !== "global") data.hidden = true; // A bug has occurred where the hidden value is sometimes set to true for entities. This shouldn't happen, but this will clean it up

    if (typeof data.definedIn === "undefined") {
      // It's not defined anywhere - which should mean it does not have any edges
      const children = rule_graph.successors(data.id);
      if (children && children.length > 0) {
        // For some reason it has children
        for (const c of children) {
          rule_graph.removeEdge(data.id, c);
        }
      }

      // if there are other nodes with the same description, remove this one
      if (data.description) {
        const duplicate = find(
          rule_graph._nodes,
          (other) =>
            other.id !== data.id &&
            Boolean(
              data.description &&
              other.description &&
              normalizeText(data.description).toLowerCase() === normalizeText(other.description).toLowerCase(),
            ),
        );
        if (duplicate) {
          pruneNode(rule_graph, data);
          removed.push({...data});
          continue;
        }
      }

      if (data.conditions || data.rows || data.value || data.otherwise || data.inferred) {
        data.conditions = undefined;
        data.rows = undefined;
        data.value = undefined;
        data.otherwise = undefined;
        data.inferred = undefined;
        rule_graph.setNode(n, data);
      }
    }

    if (data.identifier || data.fk) {
      /*if (data.hidden && data.identifier) {
        //console.log(entity_map)
        // identifiers can't have values
        data.value = undefined;
        data.condition = undefined;
        data.otherwise = undefined;
        data.rows = undefined;
        data.definedIn = undefined;
      }*/

      let noLongerIdentifier = false;

      if (data.identifier && relationships) {
        const relationship = relationships.find(other => other.textSingular === data.description);
        if (!relationship) {
          if (!data.usedIn?.length) {
            rule_graph.removeNode(data.id);
            continue;
          }

          data.identifier = undefined;
          noLongerIdentifier = true;
        }
      }

      if (!noLongerIdentifier) {
        const duplicates = filter(rule_graph._nodes, {description: data.description});
        for (const duplicateNode of duplicates) {
          if (duplicateNode.id !== data.id) {
            rule_graph.removeNode(duplicateNode.id);
            removed.push({...duplicateNode});
            log("cleanGraph", `Stripping duplicate node '${data.description}'`, {node: data}, undefined, {
              showInTest: false,
            });
          }
        }

        if (data.usedIn) {
          if (data.definedIn !== documentId && data.usedIn.indexOf(documentId) > -1) {
            // The attribute was recorded as being used in this document
            if (used_nodes.indexOf(n) > -1) {
              // It was used, and still is. Shouldn't need to do anything
              // The used_nodes contains the list of nodes in the sub_graph, eg: the nodes that are used by rules in the xgen (even though they may be in other documents)
            } else {
              // It's no longer used in this document (likely due to the user deleting the node from the document)
              pull(data.usedIn, documentId);
            }
          }
        }

        rule_graph.setNode(n, data); // Lock in removing the xgen
        continue; // Ignore - never remove identifiers
      }
    }

    if (data.definedIn === documentId) {
      const sub_node = sub_graph.node(n);
      //console.log('sub_node', sub_node)
      //if (data.description === 'the country paying the employee\'s salary') console.log('was defined', data.description, sub_graph.node(data.id));
      if (!sub_node || (sub_node && typeof sub_node.definedIn === "undefined")) {
        // THe attribute was once defined in this document, but now it's no longer defined here (maybe they changed a conclusion in a sheet into a condition)
        // Strip any conclusion data from this
        log(
          "cleanGraph",
          `Stripping old node as no longer found in sub graph (or changed from conclusion to condition)'${data.description}'`,
          { node: data, sub_node },
          undefined,
          {
            showInTest: true,
          },
        );
        data.conditions = undefined;
        data.rows = undefined;
        data.value = undefined;
        data.otherwise = undefined;
        data.definedIn = undefined;
        data.reference = undefined;
        rule_graph.setNode(n, data);
        const children = rule_graph.successors(n);
        if (children) {
          for (const c of children) {
            rule_graph.removeEdge(n, (c as any).id ?? c);
          }
        }
      }
    }

    if (data.usedIn instanceof Set) {
      data.usedIn = Array.from(data.usedIn);
    }

    if (data.usedIn) {
      // First clean this of any deleted documents
      if (documents !== undefined) {
        for (const d of data.usedIn) {
          if (allDocIds.indexOf(d) === -1) pull(data.usedIn, d);
        }
      }
      if (data.usedIn.indexOf(documentId) > -1) {
        if (data.definedIn === documentId) {
          rule_graph.setNode(n, data);
          continue;
        }
        // The attribute was recorded as being used in this document
        if (used_nodes.indexOf(n) > -1) {
          // It was used, and still is. Shouldn't need to do anything
          // The used_nodes contains the list of nodes in the sub_graph, eg: the nodes that are used by rules in the xgen (even though they may be in other documents)
        } else {
          // It's no longer used in this document (likely due to the user deleting the node from the document)
          pull(data.usedIn, documentId);
          if (data.usedIn.length === 0) {
            // Not used in any other documents any more - remove the node
            log(
              "cleanGraph",
              "Stripping node as was used in this document, but no longer is and not used anywhere else",
            );
            pruneNode(rule_graph, data);
            removed.push({ ...data });
            continue;
          }
        }
      } else if (data.usedIn.length === 0 && !data.definedIn) {
        // It's not defined or used anywhere
        log("cleanGraph", `Stripping old node as no longer defined or used anywhere '${data.description}'`, {
          node: data,
        });
        pruneNode(rule_graph, data);
        removed.push({ ...data });
        continue;
      }
    }
    // Final orphan check
    if (documents !== undefined && data.definedIn && allDocIds.indexOf(data.definedIn) === -1) {
      // It's not defined in any document, but it could be an input node
      data.definedIn = undefined;
      if (!data.usedIn?.length) {
        // It's not used anywhere either
        log("cleanGraph", "Stripping node as belongs to a deleted document and is no longer used anywhere", data);
        pruneNode(rule_graph, data);
        removed.push({ ...data });
        continue;
      }
    }

    rule_graph.setNode(n, data); // Applying any updates
  }

  return removed;
};
